import React, { createContext, useContext, useState, useCallback, useEffect } from "react";

const ComparisonContext = createContext();

export const useComparison = () => {
  const context = useContext(ComparisonContext);
  if (!context) {
    throw new Error("useComparison must be used within ComparisonProvider");
  }
  return context;
};

export const ComparisonProvider = ({ children }) => {
  const [selectedProducts, setSelectedProducts] = useState(() => {
    try {
      const saved = localStorage.getItem("comparison_products");
      return saved ? JSON.parse(saved) : [];
    } catch (err) {
      console.error("Failed to load comparison products:", err);
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("comparison_products", JSON.stringify(selectedProducts));
  }, [selectedProducts]);

  const addProduct = useCallback((product) => {
    setSelectedProducts((prev) => {
      // Check if product already exists
      if (prev.some((p) => p._id === product._id)) {
        return prev;
      }
      // Limit to 5 products max
      if (prev.length >= 5) {
        return prev;
      }
      return [...prev, product];
    });
  }, []);

  const removeProduct = useCallback((productId) => {
    setSelectedProducts((prev) => prev.filter((p) => p._id !== productId));
  }, []);

  const toggleProduct = useCallback((product) => {
    setSelectedProducts((prev) => {
      const exists = prev.some((p) => p._id === product._id);
      if (exists) {
        return prev.filter((p) => p._id !== product._id);
      } else {
        if (prev.length >= 5) {
          return prev; // Don't add if limit reached
        }
        return [...prev, product];
      }
    });
  }, []);

  const clearProducts = useCallback(() => {
    setSelectedProducts([]);
  }, []);

  const isSelected = useCallback(
    (productId) => {
      return selectedProducts.some((p) => p._id === productId);
    },
    [selectedProducts]
  );

  const value = {
    selectedProducts,
    addProduct,
    removeProduct,
    toggleProduct,
    clearProducts,
    isSelected,
  };

  return (
    <ComparisonContext.Provider value={value}>
      {children}
    </ComparisonContext.Provider>
  );
};
